import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    loadFeedback?: () => void;
  }
}

type T_props = {
  apiKey?: string;
  code?: string;
  triggers?: {
    exitIntent?: boolean;
    timeOnPage?: number; // in seconds
    forceVisibility?: boolean; // force visibility trigger
  };
  useExistingInstance?: boolean;
};

export const CemtrickScript = ({
  apiKey,
  code,
  triggers: {
    exitIntent = false,
    timeOnPage = 0,
    forceVisibility = false,
  } = {},
  useExistingInstance = false,
}: T_props) => {
  const [init, setInit] = useState(false);
  const triggeredByExitIntent = useRef(false);
  const triggeredByTimeOnPage = useRef(false);
  const triggeredForceVisibility = useRef(false);

  // 👇 nuevo: manejamos la visibilidad al pasar el mouse
  const [isCemtrickVisible, setIsCemtrickVisible] = useState(false);
  const [delayTrigger, setDelayTrigger] = useState(false);

  const isInterceptorSubmitted = () => {
    return !!sessionStorage.getItem(`interceptor-1.0.0-id`);
  };

  // Exit intent
  useEffect(() => {
    let timerId: NodeJS.Timeout;
    const mouseLeaveHandler = function (e: MouseEvent) {
      if (
        e.clientY <= 0 &&
        !triggeredByExitIntent.current &&
        window?.loadFeedback &&
        !isInterceptorSubmitted()
      ) {
        timerId = setTimeout(function () {
          window?.loadFeedback && window.loadFeedback();
          triggeredByExitIntent.current = true;
        }, 500);
      }
    };

    const mouseEnterHandler = function () {
      clearTimeout(timerId);
    };

    if (init && exitIntent) {
      document.addEventListener("mouseleave", mouseLeaveHandler);
      document.addEventListener("mouseenter", mouseEnterHandler);
    }

    return () => {
      if (exitIntent) {
        document.removeEventListener("mouseleave", mouseLeaveHandler);
        document.removeEventListener("mouseenter", mouseEnterHandler);
      }
    };
  }, [init]);

  // Time on page
  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (init && timeOnPage > 0) {
      timerId = setTimeout(() => {
        if (
          !triggeredByTimeOnPage.current &&
          window?.loadFeedback &&
          !isInterceptorSubmitted()
        ) {
          window?.loadFeedback();
          triggeredByTimeOnPage.current = true;
        }
      }, timeOnPage * 1000);
    }

    return () => {
      if (timeOnPage > 0) {
        clearTimeout(timerId);
      }
    };
  }, [init]);

  // 👇 forceVisibility manual + delay
  useEffect(() => {
    if (
      init &&
      (forceVisibility || delayTrigger) &&
      !triggeredForceVisibility.current &&
      window?.loadFeedback &&
      !isInterceptorSubmitted()
    ) {
      window?.loadFeedback();
      triggeredForceVisibility.current = true;
    }
  }, [init, forceVisibility, delayTrigger]);

  // Carga del script
  useEffect(() => {
    const inlineScript = document.createElement("script");
    inlineScript.setAttribute(
      "src",
      `https://interceptor.cemtrik.com/interceptor.js?code=${code}&key=${apiKey}`
    );

    if (!useExistingInstance) {
      document.body.appendChild(inlineScript);
    }

    setInit(true);

    return () => {
      if (!useExistingInstance) {
        document.body.removeChild(inlineScript);
        document.querySelector('div[style*="z-index: 99998"]')?.remove();
      }
    };
  }, []);

  // 👇 cuando entra el mouse, inicia el contador de 30s (cámbialo a 90000 si quieres 90s)
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCemtrickVisible) {
      timer = setTimeout(() => {
        setDelayTrigger(true);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [isCemtrickVisible]);

  // ⚡ API pública: permite a PropertyInfo activar el mouse enter
  useEffect(() => {
  if (typeof window !== "undefined") {
      (window as any).setCemtrickVisible = setIsCemtrickVisible;
    }
  }, []);

  return null;
};
